// App.tsx — first-run guard + route table for the authz SPA.
import { useEffect, useState, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Spinner from 'react-bootstrap/Spinner'
import Login from './pages/Login'
import Register from './pages/Register'
import Setup from './pages/Setup'
import Consent from './pages/Consent'
import Callback from './pages/Callback'
import CliAuth from './pages/CliAuth'
import IdentityPage from './pages/IdentityPage'
import RolesPage from './pages/RolesPage'
import RoleDetailPage from './pages/RoleDetailPage'
import ScopesPage from './pages/ScopesPage'
import ScopeDetailPage from './pages/ScopeDetailPage'
import UsersPage from './pages/UsersPage'
import UserDetailPage from './pages/UserDetailPage'
import SecretsPage from './pages/SecretsPage'
import SettingsPage from './pages/SettingsPage'
import SsoIntegrationsPage from './pages/SsoIntegrationsPage'
import AuthzShell from './components/AuthzShell'
import { palette } from 'common'
import { AUTHZ_PRESET } from './theme'

palette.bootstrapStoredPalette(undefined, AUTHZ_PRESET)

const Shelled = ({ children }: { children: ReactNode }) => <AuthzShell>{children}</AuthzShell>

function App() {
    const [firstRun, setFirstRun] = useState<boolean | null>(null)

    useEffect(() => {
        ;(async () => {
            try {
                const res = await fetch('/api/setup/status')
                if (res.ok) {
                    const data = await res.json()
                    setFirstRun(Boolean(data.first_run))
                    return
                }
            } catch {
                // fall through
            }
            setFirstRun(false)
        })()
    }, [])

    if (firstRun === null) {
        return (
            <div className="auth-wrapper">
                <Spinner animation="border" />
            </div>
        )
    }

    if (firstRun) {
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/setup" element={<Setup />} />
                    <Route path="*" element={<Navigate to="/setup" replace />} />
                </Routes>
            </BrowserRouter>
        )
    }

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/setup" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/consent" element={<Consent />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/cli-auth/:id" element={<CliAuth />} />
                <Route path="/dashboard" element={<Shelled><IdentityPage /></Shelled>} />
                <Route path="/roles" element={<Shelled><RolesPage /></Shelled>} />
                <Route path="/roles/:name" element={<Shelled><RoleDetailPage /></Shelled>} />
                <Route path="/scopes" element={<Shelled><ScopesPage /></Shelled>} />
                <Route path="/scopes/:name" element={<Shelled><ScopeDetailPage /></Shelled>} />
                <Route path="/users" element={<Shelled><UsersPage /></Shelled>} />
                <Route path="/users/:id" element={<Shelled><UserDetailPage /></Shelled>} />
                <Route path="/secrets" element={<Shelled><SecretsPage /></Shelled>} />
                <Route path="/sso" element={<Shelled><SsoIntegrationsPage /></Shelled>} />
                <Route path="/settings" element={<Shelled><SettingsPage /></Shelled>} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        </BrowserRouter>
    )
}

export default App
